import { useRuntimeConfig } from 'nuxt/app';
import { useAuthStore } from "@stores/auth";
import { useLegitimuzStatusStore } from "@stores/legitimuz-status";
import { useAlert } from '@stores/alert';
import { storeToRefs } from 'pinia';
interface AuthStore {
  user: {
    document?: {
      number?: string;
    };
  } | null;
}

interface LegitimuzEvent {
  name: 'facematch' | 'modal' | string;
  status: 'success' | 'closed' | string;
}

interface FaceIndexEvent {
  name: string;
  status: string;
}

interface LegitimuzConfig {
  host: string;
  token: string;
  eventHandler: (event: LegitimuzEvent) => Promise<void>;
}

interface LegitimuzFaceIndexConfig {
  host: string;
  appURL: string;
  token: string;
  eventHandler: (event: FaceIndexEvent) => Promise<void>;
}

interface LegitimuzSDK {
  mount: () => void;
  closeModal: () => void;
}

interface FaceIndexSDK {
  mount: () => void;
  closeModal: () => void;
}

declare global {
  interface Window {
    Legitimuz: (config: LegitimuzConfig) => LegitimuzSDK;
    LegitimuzFaceIndex: (config: LegitimuzFaceIndexConfig) => FaceIndexSDK;
    sdkInstance: FaceIndexSDK;
  }
}

interface UseLegitimuzVerificationReturn {
  showVerificationButton: Ref<boolean>;
  isLegitimuzEnabled: Ref<boolean>;
  legitimuzVerified: Ref<boolean>;
  legitimuzCpf: Ref<string>;
  userStatus: Ref<string>;
  checkLegitimuzStatus: (force?: boolean) => Promise<boolean>;
  triggerVerification: () => void;
  initVerification: (isLoggedIn: boolean) => Promise<boolean>;
  initFaceIndex: () => Promise<void>;
  isVPNDetected: Ref<boolean>;
  triggerFaceIndexVerification: () => void;
  isScriptLoaded: Ref<boolean>;
  isVerificationInProgress: Ref<boolean>;
}

export const useLegitimuzVerification = (): UseLegitimuzVerificationReturn => {
  const authStore = useAuthStore();
  const alertStore = useAlert();
  const legitimuzStore = useLegitimuzStatusStore();
  const { userStatus, isVPNDetected: storeIsVPNDetected } = storeToRefs(legitimuzStore);
  const showVerificationButton = ref<boolean>(false);
  const runtimeConfig = useRuntimeConfig();
  const legitimuz = ref<LegitimuzSDK | null>(null);
  const isVPNDetected = ref<boolean>(false);
  const legitimuzVerified = ref<boolean>(false);
  const legitimuzCpf = ref<string>(authStore.user?.document?.number || '');

  // Face Index specific refs
  const isScriptLoaded = ref<boolean>(false);
  const isVerificationInProgress = ref<boolean>(false);

  const isLegitimuzEnabled = computed((): boolean => {
    return !!runtimeConfig.public.LEGITIMUZ_API_TOKEN;
  });
  

  watch(storeIsVPNDetected, (newValue) => {
    isVPNDetected.value = newValue;
  });

  const checkLegitimuzStatus = async (force: boolean = false): Promise<boolean> => {
    return legitimuzStore.checkStatus(force);
  };
  const ensureRequiredElements = (): void => {
    if (!document.getElementById('legitimuz-hydrate-cpf')) {
      const cpfInput = document.createElement('input');
      cpfInput.id = 'legitimuz-hydrate-cpf';
      cpfInput.type = 'tel';
      cpfInput.style.display = 'none';
      cpfInput.value = legitimuzCpf.value;
      document.body.appendChild(cpfInput);
    } else {
      const cpfInput = document.getElementById('legitimuz-hydrate-cpf');
      if (cpfInput) {
        cpfInput.value = legitimuzCpf.value;
      }
    }

    if (!document.getElementById('legitimuz-action-verify')) {
      const verifyButton = document.createElement('button');
      verifyButton.id = 'legitimuz-action-verify';
      verifyButton.style.display = 'none';
      document.body.appendChild(verifyButton);
    }

    if (!document.getElementById('legitimuz-root')) {
      const root = document.createElement('div');
      root.id = 'legitimuz-root';
      document.body.appendChild(root);
    }
  };

  const initLegitimuz = (): void => {
    if (!isLegitimuzEnabled.value) return;

    ensureRequiredElements();

    const script = document.createElement('script');
    script.src = 'https://cdn.legitimuz.com/js/sdk/legitimuz-sdk.js';
    script.async = true; 
    script.onload = () => {
      legitimuz.value = window.Legitimuz({
        host: 'https://api.legitimuz.com',
        token: runtimeConfig.public.LEGITIMUZ_API_TOKEN,
        eventHandler: async (event: LegitimuzEvent) => {
          if (event.name === 'facematch') { 
            if (event.status === 'success') {
              legitimuzVerified.value = true;
              legitimuz.value?.closeModal();
              alertStore.showAlert('success', 'Verificação KYC concluída com sucesso!'); 
              await checkLegitimuzStatus(true); 
            } else if (event.status === 'closed') {
               alertStore.showAlert('info', 'Verificação KYC fechada.'); 
            } else {
               alertStore.showAlert('error', `Falha na verificação KYC: ${event.status}`); 
            }}  
        }
      });
      legitimuz.value.mount();

      const verifyButton = document.getElementById('legitimuz-action-verify');
      if (verifyButton) {
        verifyButton.click();
      }
    };
    document.body.appendChild(script);
  };

  const triggerVerification = (): void => {
    if (import.meta.client) {
      initLegitimuz();
    }
  };

  // Face Index specific functions
  const loadFaceIndexScript = (): Promise<void> => {
    if (!runtimeConfig.public.LEGITIMUZ_API_TOKEN) {
      console.error('Legitimuz API token not found!');
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      if (document.querySelector('script[src*="faceindex.js"]')) {
        isScriptLoaded.value = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.legitimuz.com/js/sdk/faceindex.js';
      script.async = true;

      script.onload = () => {
        isScriptLoaded.value = true;
        resolve();
      };

      script.onerror = (error: Event | string) => {
        console.error('Error loading Face Index SDK:', error);
        reject(error);
      };

      document.head.appendChild(script);
    });
  };

  const initFaceIndex = async (): Promise<void> => {
    if (!import.meta.client) return;

    if (!runtimeConfig.public.LEGITIMUZ_API_TOKEN) {
      console.error('Legitimuz API token not found');
      return;
    }

    try {
      await loadFaceIndexScript();
      await new Promise((resolve) => setTimeout(resolve, 100));

      window.sdkInstance = window.LegitimuzFaceIndex({
        host: 'https://api.legitimuz.com',
        appURL: 'https://widget.legitimuz.com',
        token: runtimeConfig.public.LEGITIMUZ_API_TOKEN,
        eventHandler: async (event: FaceIndexEvent) => {
          if (event.name === 'faceindex') {
            if (event.status === 'success') {
              legitimuzVerified.value = true;
              window.sdkInstance.closeModal();
              alertStore.showAlert('success', 'Verificação facial concluída com sucesso!'); 
              await checkLegitimuzStatus(true); 
            } else if (event.status === 'closed') {
                 alertStore.showAlert('info', 'Verificação facial fechada.'); 
            } else {
               alertStore.showAlert('error', `Falha na verificação facial: ${event.status}`); 
            }
            isVerificationInProgress.value = false; 
          }
        }
      });

      window.sdkInstance.mount();
    } catch (error) {
      alertStore.showAlert('error', 'Erro ao inicializar verificação facial.');
      isVerificationInProgress.value = false;
    }
  };

  const triggerFaceIndexVerification = (): void => {
    if (isVerificationInProgress.value) return;

    const cpfInput = document.getElementById('legitimuz-hydrate-cpf') as HTMLInputElement;
    if (cpfInput) {
      cpfInput.value = legitimuzCpf.value;
    }

    const button = document.getElementById('legitimuz-action-faceindex');
    if (button) {
      button.click();
    }
  };

  const initVerification = async (isLoggedIn: boolean): Promise<boolean> => {
    if (!import.meta.client) return true;
    if (isLoggedIn && isLegitimuzEnabled.value) {
      const isApproved = await checkLegitimuzStatus(true);
      if (isApproved) {
        console.log("User already approved by Legitimuz.");
        alertStore.showAlert('success', 'Sua conta já está verificada.');
        return true; 
    }
      if (!isApproved) {
        if (userStatus.value === 'pending_face_id') {
          
          await initFaceIndex();
          triggerFaceIndexVerification();
        } else if (userStatus.value === 'pending_kyc') {
          triggerVerification();
        }
      }
  
      return isApproved;
    }

    return true;
  };

  watch(
    () => authStore.user,
    (newUser) => {
      if (newUser && import.meta.client) {
        legitimuzCpf.value = newUser.document?.number || '';
        const cpfInput = document.getElementById('legitimuz-hydrate-cpf');
        if (cpfInput) {
          cpfInput.value = legitimuzCpf.value;
        }
      }
    }
  );

  watch(
    () => userStatus.value,
    (newStatus) => {
      isVPNDetected.value = newStatus === 'ip_blocked';
    }
  );



  return {
    showVerificationButton,
    isLegitimuzEnabled,
    legitimuzVerified,
    legitimuzCpf,
    userStatus,
    checkLegitimuzStatus,
    triggerVerification,
    initVerification,
    initFaceIndex,
    triggerFaceIndexVerification,
    isScriptLoaded,
    isVerificationInProgress,
    isVPNDetected
  };
};